import React, {useMemo, useState} from 'react';
import {Box, Button, Image, Input, Spinner, Text, VStack} from '@chakra-ui/react';
import {useForm} from '../../uses/useForm';
import {useApi} from '../../uses/useApi';
import {useLanguage} from '@/contexts/languageContext';
import logo from '@/assets/images/logo-cartouche-secondary.png'

export const ResetPassword = () => {
    const api = useApi();
    const [isSent, setIsSent] = useState(false);
    const { t } = useLanguage();

    const emailForm = useForm(
        {email: ''},
        {
            email: {
                required: {
                    value: true,
                    message: t('resetPassword.emailRequired')
                }
            }
        }
    );

    const passwordForm = useForm(
        {password: ''},
        {
            password: {
                required: {
                    value: true,
                    message: t('resetPassword.passwordRequired')
                }
            }
        }
    );

    const token = useMemo(() => {
        if (typeof window === 'undefined') {
            return null;
        }
        return new URLSearchParams(window.location.search ?? '').get('token');
    }, []);

    const passwordErrorMessage = useMemo(() => {
        const raw = passwordForm.formFieldErrors.password;
        if (raw === 'INVALID_PASSWORD') {
            return t('resetPassword.invalidPassword');
        }
        return raw;
    }, [passwordForm.formFieldErrors.password, t]);

    const submitEmail = () => emailForm.handleFormSubmit(
        api.AuthService.requestPasswordReset,
        () => setIsSent(true)
    );

    const submitPassword = () => passwordForm.handleFormSubmit(
        (data) => api.AuthService.confirmPasswordReset(token, data),
        () => {
            window.location.href = '/';
        }
    );

    return (
        <VStack flex={1} spacing="5vh" bg="primary.300" py="1rem" w="100%" px={{ base: '20vw', md: '20vw', lg: '30vw'}}>
            <VStack spacing="1vh">
                <Image src={logo} alt="Logo" w="auto" h="250px" />
                <Text fontSize="md" fontWeight="bold" textAlign="center" color="secondary.500">
                    {t('resetPassword.title')}
                </Text>
                {token ? (
                    <Text fontSize="sm" textAlign="center" color="secondary.500">
                        {t('resetPassword.tokenMessage')}
                    </Text>
                ) : isSent ? (
                    <Text fontSize="sm" textAlign="center" color="secondary.500">
                        {t('resetPassword.sentMessage')}
                    </Text>
                ) : (
                    <Text fontSize="sm" textAlign="center" color="secondary.500">
                        {t('resetPassword.emailMessage')}
                    </Text>
                )}
            </VStack>
            {token ? (
                <VStack spacing="2vh" w="100%" gap="2rem" m="1rem">
                    <Input
                        label={t('resetPassword.passwordLabel')}
                        type="password"
                        value={passwordForm.formData.password}
                        onChange={(e) => passwordForm.handleInputChange('password', e.target.value)}
                        placeholder={t('resetPassword.passwordPlaceholder')}
                        variant="inverted"
                        bg="white"
                        color="secondary.500"
                    />
                    {passwordErrorMessage && <Text color="red">{passwordErrorMessage}</Text>}
                    {passwordForm.getFormErrors()}
                    {passwordForm.state === 0 ? (
                        <Spinner />
                    ) : (
                        <Button
                            onClick={submitPassword}
                            variant="solid"
                            bg="secondary.500"
                            color="white"
                            borderRadius=".35rem"
                            style={{ padding:"1rem 2rem" }}
                        >
                            {t('resetPassword.passwordButton')}
                        </Button>
                    )}
                </VStack>
            ) : (!isSent && (
                <VStack spacing="2vh" w="100%" gap="2rem" m="1rem">
                    <Input
                        label={t('resetPassword.emailLabel')}
                        value={emailForm.formData.email}
                        onChange={(e) => emailForm.handleInputChange('email', e.target.value)}
                        placeholder={t('resetPassword.emailLabel')}
                        variant="inverted"
                        bg="white"
                        color="secondary.500"
                    />
                    {emailForm.formFieldErrors.email && <Text color="red">{emailForm.formFieldErrors.email}</Text>}
                    {emailForm.getFormErrors()}
                    {emailForm.state === 0 ? (
                        <Spinner />
                    ) : (
                        <Button
                            onClick={submitEmail}
                            variant="solid"
                            bg="secondary.500"
                            color="white"
                            borderRadius=".35rem"
                            style={{ padding:"1rem 2rem" }}
                        >
                            {t('resetPassword.emailButton')}
                        </Button>
                    )}
                </VStack>
            ))}
            <Button variant="link" color="secondary.500" fontSize="sm" as='a' href="/" borderRadius="0">
                {t('resetPassword.back')}
            </Button>
            <Box flex={1}/>
        </VStack>
    );
};

export default ResetPassword;
